import React, { useContext, createContext, useMemo } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Breadcrumbs as MuiBreadcrumbs, Link, Typography, Box, Tooltip } from '@mui/material';
import { Home as HomeIcon, ChevronRight as ChevronRightIcon } from '@mui/icons-material';

// Create a context to share category data with the breadcrumbs component
export const BreadcrumbContext = createContext({
  categoryName: null,
  setCategoryName: () => {}
});

// Create a provider component for the breadcrumb context
export const BreadcrumbProvider = ({ children }) => {
  const [categoryName, setCategoryName] = React.useState(null);

  const value = useMemo(() => ({ categoryName, setCategoryName }), [categoryName]);

  return (
    <BreadcrumbContext.Provider value={value}>
      {children}
    </BreadcrumbContext.Provider>
  );
};

const routeConfig = {
  '/': { label: 'Dashboard', parent: null },
  '/transactions': { label: 'Transactions', parent: '/' },
  '/transactions/trending': { label: 'Trending', parent: '/transactions' },
  '/categories': { label: 'Categories', parent: '/' },
  '/categories/:id': { label: 'Category Details', parent: '/categories' },
  '/accounts': { label: 'Accounts', parent: '/' },
  '/accounts/:id': { label: 'Account Details', parent: '/accounts' },
  '/recurring-transactions': { label: 'Recurring Transactions', parent: '/' },
  '/tags': { label: 'Tags', parent: '/' },
  '/settings': { label: 'Settings', parent: '/' },
  '/budgets': { label: 'Budgets', parent: '/' },
  '/currencies': { label: 'Currencies', parent: '/' }
};

const Breadcrumbs = () => {
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter(x => x);
  const { categoryName } = useContext(BreadcrumbContext);
  
  // Generate breadcrumb trail
  const getBreadcrumbs = () => {
    let breadcrumbs = [];
    let currentPath = '';
    
    // Always start with home
    breadcrumbs.push({
      path: '/',
      label: 'Home',
      isLast: pathnames.length === 0
    });
    
    // Add additional breadcrumbs based on the current path
    if (pathnames.length > 0) {
      pathnames.forEach((part, index) => {
        const isLast = index === pathnames.length - 1;
        
        // Build the path for this level
        currentPath += `/${part}`;
        
        // Check if this is an ID parameter (numeric or UUID)
        const isIdParam = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$|^\d+$/.test(part);
        
        if (isIdParam) {
          // For ID parameters, use the parent path pattern
          const parentPath = currentPath.split('/').slice(0, -1).join('/');
          const routePattern = `${parentPath}/:id`;
          
          if (routeConfig[routePattern]) {
            // For categories/:id, use the dynamic category name if available
            const isCategory = parentPath === '/categories';
            const label = isCategory && categoryName ? categoryName : routeConfig[routePattern].label;
            
            breadcrumbs.push({
              path: isLast ? null : currentPath, // If it's the last item, don't make it a link
              label: label,
              isLast
            });
          } else {
            // If no specific config, just capitalize the part
            breadcrumbs.push({
              path: isLast ? null : currentPath,
              label: `${part}`,
              isLast
            });
          }
        } else {
          // For normal paths
          const label = routeConfig[currentPath] 
            ? routeConfig[currentPath].label 
            : part.charAt(0).toUpperCase() + part.slice(1);
          
          breadcrumbs.push({
            path: isLast ? null : currentPath,
            label,
            isLast
          });
        }
      });
    }
    
    return breadcrumbs;
  };
  
  const breadcrumbs = getBreadcrumbs();
  
  // Don't render breadcrumbs on home page
  if (location.pathname === '/') {
    return null;
  }
  
  return (
    <Box 
      sx={{ 
        mb: 0,
        p: 1,
        px: 2,
        display: 'flex',
        alignItems: 'center',
        overflowX: 'auto', // Allow horizontal scrolling if needed
        '&::-webkit-scrollbar': {
          height: '4px'
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.2)',
          borderRadius: '4px'
        }
      }}
    >
      <MuiBreadcrumbs 
        separator={<ChevronRightIcon fontSize="small" sx={{ opacity: 0.7 }} />}
        aria-label="breadcrumb"
        maxItems={4} // Show max 4 items
        itemsAfterCollapse={2} // Show 2 items after collapse
        itemsBeforeCollapse={1} // Show 1 item before collapse
      >
        {breadcrumbs.map((breadcrumb, index) => {
          const LabelComponent = breadcrumb.isLast ? (
            <Typography 
              color="primary" 
              key={index}
              variant="body2"
              fontWeight="medium"
              sx={{
                maxWidth: '200px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {breadcrumb.label}
            </Typography>
          ) : (
            <Link 
              component={RouterLink} 
              to={breadcrumb.path} 
              key={index}
              underline="hover"
              color="text.secondary"
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                maxWidth: '200px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                '&:hover': {
                  color: 'primary.main'
                }
              }}
              variant="body2"
            >
              {index === 0 ? (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <HomeIcon fontSize="small" sx={{ mr: 0.5 }} />
                  {breadcrumb.label}
                </Box>
              ) : (
                breadcrumb.label
              )}
            </Link>
          );

          // Add tooltip for longer text
          return (
            <Tooltip title={breadcrumb.label} key={index} arrow placement="top">
              {LabelComponent}
            </Tooltip>
          );
        })}
      </MuiBreadcrumbs>
    </Box>
  );
};

export default Breadcrumbs; 